import { useAppContext } from "../lib/AppContext.tsx";
import { Bell, Settings as SettingsIcon, Home, Users, TrendingUp, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { clientNotificationSchema, ClientNotification } from "../../../shared/schema";
import { Badge } from "../components/ui/badge.tsx";
import { Button } from "../components/ui/button.tsx";
import { useToast } from "../components/ui/use-toast.ts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { ScrollArea } from "./ui/scroll-area";
import { supabase } from "../lib/supabase";

export default function TabNavigation() {
  const { auth, activeTab, setActiveTab, currentUser, isGuest, endGuestSession } = useAppContext();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    async function getToken() {
      const { data } = await supabase.auth.getSession();
      setAccessToken(data?.session?.access_token ?? null);
    }
    getToken();
  }, []);

  // Fetch notifications to show count
  const { data: notifications, refetch: refetchNotifications } = useQuery<ClientNotification[]>({
    queryKey: [`/api/users/${currentUser?.id}/notifications`, currentUser?.id, accessToken],
    enabled: !!currentUser && !!accessToken,
    queryFn: async () => {
      const res = await fetch(`/api/users/${currentUser?.id}/notifications`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch notifications");
      const raw = await res.json();
      // Map user_id to userId for each notification
      return raw.map((n: any) => ({
        ...n,
        userId: n.user_id,
      }));
    },
  });

  const unreadCount = notifications?.filter(n => !n.read).length || 0;

  // Show all notifications
  const allNotifications = notifications || [];

  // Clear all notifications handler
  const handleClearAllNotifications = async () => {
    if (!currentUser || !accessToken || !allNotifications.length) return;
    try {
      
      
      await Promise.all(
        allNotifications
          .filter(notification => {
            // Validate that notification has a valid UUID
            const isValid = typeof notification.id === 'string' && 
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(notification.id);
            if (!isValid) {
              // Skipping invalid notification ID
              return false;
            }
            return true;
          })
          .map(async (notification) => {

            
            const response = await fetch(`/api/notifications/${notification.id}`, {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
              credentials: 'include',
            });
            
                          if (!response.ok) {
                const errorText = await response.text();
                // Failed to delete notification
                return;
              }
            

          })
      );
      
      // Refetch notifications instead of reloading the page
      await refetchNotifications();
          } catch (error) {
        // Clear all notifications error
        toast({
        title: 'Error',
        description: 'Failed to clear notifications. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = async () => {
    // If user is in a guest session, end it and redirect
    if (isGuest) {
      try { endGuestSession(); } catch {}
      setLocation("/auth");
      return;
    }
    try {
      await auth.logout();
      setLocation("/auth");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTabClick = useCallback((tab: string) => {
    setActiveTab(tab as any);
    setLocation(`/${tab === 'home' ? '' : tab}`);
  }, [setActiveTab, setLocation]);

  return (
    <>
      <div className="pt-safe px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent flex items-center gap-2">
            <Home className="h-6 w-6 text-blue-600" />
            My Agent
          </h1>
          <div className="flex items-center space-x-3">
            <DropdownMenu onOpenChange={setIsNotificationsOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={`shadow-sm h-9 w-9 relative rounded-full ${
                    isNotificationsOpen ? 'bg-blue-100' : 'bg-white'
                  }`}
                >
                  <Bell className="h-5 w-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <Badge
                      className="absolute -top-1 -right-1 px-1 min-w-[1.25rem] h-5"
                      variant="destructive"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex justify-between items-center">
                  <span>Notifications</span>
                  {allNotifications.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-red-600 px-2 py-1"
                      onClick={handleClearAllNotifications}
                    >
                      Clear All
                    </Button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ScrollArea className="h-[300px]">
                  {allNotifications.length > 0 ? (
                    allNotifications.map((notification) => (
                      <DropdownMenuItem
                        key={notification.id}
                        className="group flex flex-col items-start p-4 cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
                        onClick={async () => {
                          const type = (notification.type || '').toLowerCase();
                          const title = (notification.title || '').toLowerCase();
                          const msg = (notification.message || '').toLowerCase();
                          const isFriend =
                            type.includes('friend') ||
                            type.includes('connection') ||
                            title.includes('friend') ||
                            title.includes('connection') ||
                            msg.includes('friend') ||
                            msg.includes('connection');
                          if (isFriend) {
                            setLocation('/friends');
                          } else if (type.includes('message')) {
                            setLocation('/messages');
                          } else if (type.includes('appointment') || title.includes('appointment')) {
                            setLocation('/schedule');
                          } else if (type.includes('transaction') || title.includes('transaction')) {
                            setLocation('/transactions');
                          } else {
                            setLocation('/schedule');
                          }
                          // Ensure schedule data refreshes immediately
                          try { await refetchNotifications(); } catch {}
                        }}
                      >
                        <div className="flex items-start gap-2 w-full">
                          <div className={`w-2 h-2 rounded-full mt-2 ${notification.read ? 'bg-gray-300' : 'bg-blue-600'}`} />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{notification.title}</p>
                            <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {notification.createdAt && !isNaN(new Date(notification.createdAt).getTime())
                                ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
                                : ""}
                            </p>
                          </div>
                          <button
                            className="opacity-0 group-focus:opacity-100 group-hover:opacity-100 transition-opacity ml-2 p-1 rounded hover:bg-gray-100"
                            aria-label="Dismiss notification"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!notification.id || !accessToken) return;
                              // Validate UUID
                              const isValid = typeof notification.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(notification.id);
                              if (!isValid) return;
                              const response = await fetch(`/api/notifications/${notification.id}`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${accessToken}` },
                                credentials: 'include',
                              });
                              if (response.ok) {
                                await refetchNotifications();
                              }
                            }}
                          >
                            <X className="h-4 w-4 text-gray-400" />
                          </button>
                        </div>
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No notifications
                    </div>
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu onOpenChange={setIsSettingsOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={`shadow-sm h-9 w-9 rounded-full ${
                    isSettingsOpen ? 'bg-blue-100' : 'bg-white'
                  }`}
                >
                  <SettingsIcon className="h-5 w-5 text-gray-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLocation("/profile")}>
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLocation("/change-email")}>
                  Change Email
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLocation("/change-password")}>
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <div className="px-2 bg-white border-b border-gray-200">
        <div className="flex justify-between">
          <button
            className={`px-4 py-3 font-medium text-center flex-1 ${
              activeTab === "home" ? "active-tab text-primary" : "text-gray-500"
            }`}
            onClick={() => handleTabClick('home')}
          >
            <span>Home</span>
          </button>
          <button
            className={`px-4 py-3 font-medium text-center flex-1 ${
              activeTab === "favorites" ? "active-tab text-primary" : "text-gray-500"
            }`}
            onClick={() => handleTabClick('favorites')}
          >
            <span>Favorites</span>
          </button>
          <button
            className={`px-4 py-3 font-medium text-center flex-1 ${
              activeTab === "schedule" ? "active-tab text-primary" : "text-gray-500"
            }`}
            onClick={() => handleTabClick('schedule')}
          >
            <span>Schedule</span>
          </button>
          <button
            className={`px-4 py-3 font-medium text-center flex-1 ${
              activeTab === "messages" ? "active-tab text-primary" : "text-gray-500"
            }`}
            onClick={() => handleTabClick('messages')}
          >
            <span>Messages</span>
          </button>
        </div>
      </div>
    </>
  );
}